import React, { useMemo, useState } from 'react';
import { kpiThemes, KpiStatus, CatalogKpi, hasData } from '../data/kpiCatalog';

// Theme-stable status styles (inline rgba so they read on both cream & ink).
const STATUS: Record<KpiStatus, { label: string; fg: string; bg: string }> = {
  live: { label: 'Live', fg: '#ba9965', bg: 'rgba(186,153,101,0.16)' },
  dataAvailable: {
    label: 'Data available',
    fg: '#0a8754',
    bg: 'rgba(10,135,84,0.16)',
  },
  blocked: {
    label: 'Blocked on source',
    fg: '#b3432f',
    bg: 'rgba(179,67,47,0.14)',
  },
  scoping: {
    label: 'Being defined',
    fg: '#c08b12',
    bg: 'rgba(210,153,34,0.16)',
  },
  none: {
    label: 'Not yet available',
    fg: 'var(--color-on-surface-tertiary)',
    bg: 'var(--color-surface-tertiary)',
  },
};

const FILTERS: Array<{
  key: string;
  label: string;
  match: (s: KpiStatus) => boolean;
}> = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'have', label: 'Has data', match: hasData },
  { key: 'live', label: 'Live now', match: (s) => s === 'live' },
  { key: 'blocked', label: 'Blocked on source', match: (s) => s === 'blocked' },
  { key: 'scoping', label: 'Being defined', match: (s) => s === 'scoping' },
  { key: 'none', label: 'Not yet available', match: (s) => s === 'none' },
];

const StatusPill: React.FC<{ status: KpiStatus }> = ({ status }) => {
  const s = STATUS[status];
  return (
    <span
      className='inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium'
      style={{ color: s.fg, background: s.bg }}
    >
      <span className='h-1.5 w-1.5 rounded-full' style={{ background: s.fg }} />
      {s.label}
    </span>
  );
};

export const KpiChip: React.FC<{ kpi: CatalogKpi }> = ({ kpi }) => {
  const linked = !!kpi.anchor;
  const body = (
    <>
      <div className='flex items-start justify-between gap-2'>
        <span className='text-[13px] font-medium leading-snug text-on-surface'>{kpi.name}</span>
        <StatusPill status={kpi.status} />
      </div>
      <div className='mt-2 flex items-center gap-1.5 text-[11px] text-on-surface-quaternary'>
        <span className='rounded bg-surface-tertiary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-on-surface-tertiary'>
          {kpi.team}
        </span>
        <span className='truncate'>{kpi.origin ? `Source: ${kpi.origin}` : 'No source yet'}</span>
        {linked && <span className='ml-auto shrink-0 text-on-surface-tertiary'>↘ chart</span>}
      </div>
    </>
  );
  const cls = 'block rounded-lg border border-theme-border bg-surface p-3 transition-colors';
  return linked ? (
    <a href={`#${kpi.anchor}`} className={cls + ' hover:border-[color:var(--color-primary)]'}>
      {body}
    </a>
  ) : (
    <div className={cls}>{body}</div>
  );
};

export const KpiCatalog: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const match = FILTERS.find((f) => f.key === filter)!.match;

  const totals = useMemo(() => {
    const all = kpiThemes.flatMap((t) => t.kpis);
    return {
      total: all.length,
      live: all.filter((k) => k.status === 'live').length,
      dataAvailable: all.filter((k) => k.status === 'dataAvailable').length,
      blocked: all.filter((k) => k.status === 'blocked').length,
      none: all.filter((k) => k.status === 'none').length,
    };
  }, []);

  return (
    <section id='kpi-master' className='mt-12 scroll-mt-20'>
      <div className='mb-1 flex flex-wrap items-end justify-between gap-3'>
        <h2
          className='text-2xl font-semibold tracking-tight text-on-surface'
          style={{ fontFamily: "'Spectral', Georgia, serif" }}
        >
          All KPIs by business area
        </h2>
        <div className='text-[13px] text-on-surface-secondary'>
          {totals.total} KPIs · <span style={{ color: '#ba9965' }}>{totals.live} live</span> ·{' '}
          <span style={{ color: '#0a8754' }}>{totals.dataAvailable} data available</span> ·{' '}
          <span style={{ color: '#b3432f' }}>{totals.blocked} blocked on source</span> ·{' '}
          {totals.none} not yet available
        </div>
      </div>
      <p className='mb-4 max-w-3xl text-[13px] leading-relaxed text-on-surface-tertiary'>
        Every metric the organization tracks, grouped by what it measures rather than by team. The
        owning team is tagged on each. Live metrics link to their chart above. "Data available"
        means the data is already ingested here and just needs a chart; "blocked on source" means
        the underlying system still needs to be connected to Canon first.
      </p>

      {/* filter chips */}
      <div className='mb-6 flex flex-wrap gap-2'>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
              (filter === f.key
                ? 'border-transparent bg-surface-tertiary text-on-surface'
                : 'border-theme-border text-on-surface-secondary hover:text-on-surface')
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className='space-y-8'>
        {kpiThemes.map((group) => {
          const shown = group.kpis.filter((k) => match.call(null, k.status));
          if (!shown.length) return null;
          return (
            <div key={group.theme}>
              <div className='mb-3 flex items-baseline gap-2 border-b border-theme-border pb-2'>
                <h3 className='text-sm font-semibold uppercase tracking-wide text-on-surface'>
                  {group.theme}
                </h3>
                <span className='text-[11px] text-on-surface-quaternary'>
                  {shown.length}
                  {shown.length !== group.kpis.length ? ` / ${group.kpis.length}` : ''}
                </span>
              </div>
              <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                {shown.map((k) => (
                  <KpiChip key={k.name} kpi={k} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
